(() => {
  if (window.KpUtsavPdpJourney) return;

  const roots = new Map();
  const pairings = new Map();
  const priceLabels = new Map();
  const parameters = () => new URL(window.location.href).searchParams;
  const explicitContext = () => parameters().get('kp_utsav') === '1';
  const legacyBuilderPaths = new Set([
    '/pages/combo-page', '/pages/everyday-combos', '/pages/ootd-combos',
    '/pages/trending-combos', '/pages/top-earrings-combos', '/pages/festive-combo-page'
  ]);

  const isActive = () => {
    try {
      return typeof window.KpUtsavJourney?.isActive === 'function'
        ? window.KpUtsavJourney.isActive() === true : explicitContext();
    } catch (_) {
      return explicitContext();
    }
  };

  const safeBuilderUrl = value => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.searchParams.getAll('view').length > 1) return null;
      const isBuilder = (url.pathname === '/pages/utsav-combo'
        && (!url.searchParams.has('view') || url.searchParams.get('view') === 'utsav-combo'))
        || (url.pathname === '/pages' && url.searchParams.get('view') === 'utsav-combo')
        || (legacyBuilderPaths.has(url.pathname) && !url.searchParams.has('view'));
      return url.origin === window.location.origin && !url.username && !url.password && isBuilder ? url : null;
    } catch (_) {
      return null;
    }
  };

  const updateLink = root => {
    if (!root) return;
    const link = root.querySelector('[data-kp-utsav-builder-link]');
    if (!link) return;
    let destination;
    try {
      destination = safeBuilderUrl(window.KpUtsavJourney?.builderUrl?.());
    } catch (_) {
      destination = null;
    }
    destination ||= safeBuilderUrl(parameters().get('kp_utsav_return'))
      || safeBuilderUrl(roots.get(root)?.fallbackHref);
    if (!destination) return;
    if (root.dataset.comboAddOn !== 'true') {
      destination.searchParams.set('autoselect', root.dataset.productHandle || '');
      const variant = root.querySelector('[data-kp-variant-select]')?.value
        || root.dataset.currentVariantId;
      if (/^\d+$/.test(String(variant || ''))) destination.searchParams.set('utsav_variant', variant);
    }
    link.href = destination.href;
  };

  // A cart POST is not idempotent. Preserve the exact payload across retries and
  // reconcile it before sending anything else, including after reload/back.
  const handoffStorageKey = 'kp:utsav:pdp-handoff:v1';
  const handoffProperty = '_utsav_pdp_handoff';
  let handoffInFlight = null;

  const showHandoffStatus = (root, message) => {
    const status = root.querySelector('[data-kp-product-status]');
    if (status) status.textContent = message;
  };
  const readHandoff = () => {
    try {
      const raw = window.sessionStorage.getItem(handoffStorageKey);
      if (!raw) return null;
      const saved = JSON.parse(raw);
      if (saved.version !== 1 || typeof saved.token !== 'string' || !saved.token
        || typeof saved.fingerprint !== 'string' || !['sent', 'confirmed', 'rejected'].includes(saved.phase)
        || !Array.isArray(saved.items) || saved.items.length < 1 || saved.items.length > 3
        || saved.items.some(item => !Number.isSafeInteger(item.id) || item.id < 1
          || !Number.isSafeInteger(item.quantity) || item.quantity < 1
          || item.properties?.[handoffProperty] !== saved.token)) throw new Error();
      return saved;
    } catch (_) {
      throw new Error('We could not check your previous selection. Please check your cart before trying again.');
    }
  };
  const saveHandoff = saved => {
    try {
      const raw = JSON.stringify(saved);
      window.sessionStorage.setItem(handoffStorageKey, raw);
      if (window.sessionStorage.getItem(handoffStorageKey) !== raw) throw new Error();
    } catch (_) {
      // Without a durable journal, a reload after a lost response could duplicate
      // the same selection. Do not start a cart write in that state.
      throw new Error('We could not save this selection safely. Please enable session storage and try again.');
    }
  };
  const requestHandoffCart = async (path, items) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}${path}`, {
        method: items ? 'POST' : 'GET', cache: 'no-store', credentials: 'same-origin',
        signal: controller.signal,
        headers: { Accept: 'application/json', ...(items ? { 'Content-Type': 'application/json' } : {}) },
        ...(items ? { body: JSON.stringify({ items }) } : {})
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const error = new Error(payload?.description || payload?.message || 'Could not save this selection.');
        error.httpStatus = response.status;
        throw error;
      }
      if (!items && !Array.isArray(payload?.items)) throw new Error('Could not refresh your cart.');
      return payload;
    } finally { window.clearTimeout(timer); }
  };
  const captureHandoffSelection = root => {
    if (root.dataset.utsavEligible !== 'true' || root.dataset.productClass === 'pearl-with-gold' || root.dataset.comboAddOn === 'true') {
      throw new Error('This product is not eligible for Shop Utsav.');
    }
    const select = root.querySelector('[data-kp-variant-select]');
    const option = select?.selectedOptions?.[0];
    const variant = Number(select?.value || root.dataset.currentVariantId);
    const quantity = Number(root.querySelector('[data-kp-quantity-input]')?.value || 1);
    if (!Number.isSafeInteger(variant) || variant < 1 || !Number.isSafeInteger(quantity) || quantity < 1) {
      throw new Error('Choose an available option and a whole product quantity.');
    }
    if ((option?.dataset.available || root.dataset.currentAvailable) === 'false' || option?.disabled) {
      throw new Error('This option is sold out. Please choose another option.');
    }
    const box = root.querySelector('[data-rakhi-hamper-addon]');
    const boxQuantity = Number(box?.dataset.hamperCount || 0);
    const wrap = root.querySelector('[data-kp-gift-wrap]');
    const wrapSelected = Boolean(wrap?.querySelector('[data-kp-gift-wrap-input]')?.checked);
    if (!Number.isSafeInteger(boxQuantity) || boxQuantity < 0 || boxQuantity > quantity) {
      throw new Error('Choose no more gift boxes than the product quantity.');
    }
    if (boxQuantity && typeof window.KpRakhiHamperAddon?.buildItems !== 'function'
      || wrapSelected && typeof window.KpGiftWrapAddon?.appendToItems !== 'function') {
      throw new Error('Your selected packaging is still loading. Please try again.');
    }
    const boxVariant = boxQuantity ? Number(box.dataset.hamperVariantId) : null;
    const wrapVariant = wrapSelected ? Number(wrap.dataset.giftWrapVariantId) : null;
    if ([boxVariant, wrapVariant].some(value => value !== null && (!Number.isSafeInteger(value) || value < 1))) {
      throw new Error('Your selected packaging is unavailable. Please refresh and try again.');
    }
    return { variant, quantity, boxQuantity, boxVariant, wrapSelected, wrapVariant,
      fingerprint: JSON.stringify([root.dataset.productHandle || '', variant, quantity, boxVariant, boxQuantity, wrapVariant]) };
  };
  const buildHandoff = (root, selection) => {
    const token = window.crypto?.randomUUID?.() || `utsav-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const parent = { id: selection.variant, quantity: selection.quantity, properties: {
      _combo: 'true', _combo_group: 'kp-utsav-combo-v1', _combo_source: 'utsav-pdp'
    } };
    let items = selection.boxQuantity ? window.KpRakhiHamperAddon.buildItems(root, parent) : [parent];
    if (selection.wrapSelected) items = window.KpGiftWrapAddon.appendToItems(root, items);
    if (!Array.isArray(items) || items.length !== 1 + Number(selection.boxQuantity > 0) + Number(selection.wrapSelected)
      || items[0].id !== selection.variant || items[0].quantity !== selection.quantity) {
      throw new Error('We could not prepare your selected product and packaging. Please refresh and try again.');
    }
    items = items.map((item, index) => {
      const role = index === 0 ? 'product' : String(item.properties?._rakhi_addon) === 'true' ? 'box' : 'wrap';
      const expectedId = role === 'product' ? selection.variant : role === 'box' ? selection.boxVariant : selection.wrapVariant;
      const expectedQuantity = role === 'product' ? selection.quantity : role === 'box' ? selection.boxQuantity : 1;
      if (Number(item.id) !== expectedId || item.quantity !== expectedQuantity
        || index > 0 && String(item.properties?._combo_excluded) !== 'true') {
        throw new Error('We could not prepare your selected packaging. Please refresh and try again.');
      }
      return { ...item, properties: { ...(item.properties || {}), [handoffProperty]: token, _utsav_pdp_role: role } };
    });
    return { version: 1, token, fingerprint: selection.fingerprint, items, phase: 'sent' };
  };
  const handoffCartState = (cart, saved) => {
    const lines = cart.items.filter(line => line.properties?.[handoffProperty] === saved.token);
    if (!lines.length) return 'absent';
    const exact = saved.items.every(expected => {
      const matching = lines.filter(line => Number(line.variant_id || line.id) === expected.id
        && Object.entries(expected.properties).every(([key, value]) => String(line.properties?.[key]) === String(value)));
      return matching.reduce((sum, line) => sum + Number(line.quantity || 0), 0) === expected.quantity;
    });
    const onlyExpected = lines.every(line => saved.items.some(expected => Number(line.variant_id || line.id) === expected.id
      && Object.entries(expected.properties).every(([key, value]) => String(line.properties?.[key]) === String(value))));
    return exact && onlyExpected ? 'complete' : 'partial';
  };
  const emitHandoffCart = cart => {
    window.KpUtsavJourney?.observeCart?.(cart);
    document.querySelectorAll('.js-cart-count').forEach(node => { node.textContent = String(cart.item_count || 0); });
    window.theme?.miniCart?.refreshCart?.(cart, false);
    document.dispatchEvent(new CustomEvent('kp:cart-updated', { detail: { cart } }));
  };
  const addSelectionToUtsav = root => {
    if (handoffInFlight) return handoffInFlight;
    handoffInFlight = (async () => {
      await Promise.resolve(); // Install the shared promise before any synchronous validation fails.
      const controls = Array.from(root.querySelectorAll('button, input, select'));
      const previousDisabled = controls.map(control => control.disabled);
      const link = root.querySelector('[data-kp-utsav-builder-link]');
      const priorBusy = link?.getAttribute('aria-busy');
      let locked = false;
      const journey = window.KpUtsavJourney;
      try {
        const selection = captureHandoffSelection(root);
        updateLink(root);
        const destination = safeBuilderUrl(link?.href);
        if (!destination) throw new Error('Shop Utsav is unavailable. Please refresh and try again.');
        if (journey?.isMutating?.()) throw new Error('Your cart is updating. Please try again in a moment.');
        const revision = journey?.cartRevision?.();
        controls.forEach(control => { control.disabled = true; });
        link?.setAttribute('aria-busy', 'true');
        showHandoffStatus(root, 'Saving your product and selected packaging…');
        let saved = readHandoff();
        let cart = await requestHandoffCart('cart.js');
        if (journey?.isMutating?.() || revision !== journey?.cartRevision?.()) {
          throw new Error('Your cart changed. Please try again after it finishes updating.');
        }
        if (saved) {
          const state = handoffCartState(cart, saved);
          if (state === 'partial') {
            if (saved.phase !== 'confirmed') {
              throw new Error('Your previous selection is incomplete. Please check your cart before adding again.');
            }
            const parentStillPresent = cart.items.some(line => line.properties?.[handoffProperty] === saved.token
              && line.properties?._utsav_pdp_role === 'product' && Number(line.quantity || 0) > 0);
            if (!parentStillPresent) throw new Error('Please review the packaging left in your cart before adding this selection again.');
            // A confirmed add is finished. Later manual quantity/packaging edits
            // belong to the buyer; a Back/retry must not undo them or add again.
            if (saved.fingerprint === selection.fingerprint) {
              emitHandoffCart(cart);
              destination.searchParams.delete('autoselect'); destination.searchParams.delete('utsav_variant');
              showHandoffStatus(root, 'Opening your current selection in Shop Utsav…');
              window.location.assign(destination.href);
              return { ok: true, cart, recovered: true, edited: true };
            }
            // A changed PDP selection is a new explicit add intent, not a retry
            // of the already-confirmed (and subsequently edited) cart lines.
          }
          if (state === 'absent' && saved.phase === 'sent') {
            throw new Error('We are still checking your previous add. Please check your cart, then retry. Nothing will be added twice.');
          }
          if (state === 'complete') {
            const wasPending = saved.phase === 'sent';
            saved.phase = 'confirmed'; saveHandoff(saved); emitHandoffCart(cart);
            if (saved.fingerprint !== selection.fingerprint && wasPending) {
              throw new Error('Your previous selection was saved. Please review your cart before adding the changed selection.');
            }
            if (saved.fingerprint === selection.fingerprint) {
              destination.searchParams.delete('autoselect'); destination.searchParams.delete('utsav_variant');
              showHandoffStatus(root, 'Your selection is saved. Opening Shop Utsav…');
              window.location.assign(destination.href);
              return { ok: true, cart, recovered: true };
            }
          }
        }
        saved = buildHandoff(root, selection);
        // Persist BEFORE sending: an aborted/lost response must not cause a retry POST.
        saveHandoff(saved);
        if (typeof journey?.beginMutation === 'function' && typeof journey?.endMutation === 'function') {
          journey.beginMutation(); locked = true;
        }
        let addError = null;
        try { await requestHandoffCart('cart/add.js', saved.items); } catch (error) { addError = error; }
        try { cart = await requestHandoffCart('cart.js'); }
        catch (_) { throw new Error('We could not confirm your add yet. Retry to check your cart; we will not add it twice.'); }
        const state = handoffCartState(cart, saved);
        emitHandoffCart(cart);
        if (state !== 'complete') {
          // Shopify can return422 AND add a partial quantity. Only a completed4xx
          // response plus an empty readback allows a new attempt on a later click.
          if (state === 'absent' && addError?.httpStatus >= 400 && addError.httpStatus < 500) {
            saved.phase = 'rejected'; saveHandoff(saved);
          }
          throw new Error(state === 'partial'
            ? 'Only part of your selection was saved. Please check your cart before continuing; it will not be added again.'
            : addError?.message || 'We could not confirm your selection yet. Please check your cart and retry.');
        }
        saved.phase = 'confirmed'; saveHandoff(saved);
        if (locked) { journey.endMutation(); locked = false; }
        destination.searchParams.delete('autoselect'); destination.searchParams.delete('utsav_variant');
        showHandoffStatus(root, 'Your selection is saved. Opening Shop Utsav…');
        // Keep staged controls intact until navigation. A blocked navigation or
        // Back/retry must reconcile the same fingerprint, not create a new add.
        window.location.assign(destination.href);
        return { ok: true, cart, recovered: Boolean(addError) };
      } catch (error) {
        showHandoffStatus(root, error.message || 'Could not save this selection. Please check your cart and try again.');
        return { ok: false, error: error.message };
      } finally {
        if (locked) journey?.endMutation?.();
        controls.forEach((control, index) => { control.disabled = previousDisabled[index]; });
        if (link) { if (priorBusy === null) link.removeAttribute('aria-busy'); else link.setAttribute('aria-busy', priorBusy); }
        handoffInFlight = null;
      }
    })();
    return handoffInFlight;
  };

  const refresh = () => {
    const active = isActive();
    document.querySelectorAll('[data-kp-pdp]').forEach(root => {
      if (!roots.has(root)) {
        roots.set(root, {
          buttonHidden: root.querySelector('[data-kp-add-combo]')?.hidden || false,
          defaultNoteHidden: root.querySelector('[data-kp-default-combo-note]')?.hidden || false,
          fallbackHref: root.querySelector('[data-kp-utsav-builder-link]')?.getAttribute('href') || '',
          linkText: root.querySelector('[data-kp-utsav-builder-link]')?.textContent || ''
        });
      }
      const state = roots.get(root);
      const button = root.querySelector('[data-kp-add-combo]');
      const link = root.querySelector('[data-kp-utsav-builder-link]');
      const defaultNote = root.querySelector('[data-kp-default-combo-note]');
      const note = root.querySelector('[data-kp-utsav-offer-note]');
      const canHandoff = root.dataset.utsavEligible === 'true' && root.dataset.productClass !== 'pearl-with-gold' && root.dataset.comboAddOn !== 'true';
      root.toggleAttribute('data-kp-utsav-journey', active);
      if (button && link) button.hidden = canHandoff || state.buttonHidden;
      if (link) {
        link.hidden = !canHandoff;
        link.textContent = canHandoff ? 'Shop Utsav' : state.linkText;
      }
      if (defaultNote) defaultNote.hidden = active || state.defaultNoteHidden;
      if (note) note.hidden = !active;
      if (active) {
        updateLink(root);
        const priceLabel = Array.from(root.querySelector('.kp-pdp__combo-price')?.childNodes || [])
          .find(node => node.nodeType === 3 && node.textContent.trim());
        if (priceLabel) {
          if (!priceLabels.has(priceLabel)) priceLabels.set(priceLabel, priceLabel.textContent);
          priceLabel.textContent = 'With Utsav offer ';
        }
      }
    });

    if (!active) {
      priceLabels.forEach((value, node) => {
        if (node.textContent === 'With Utsav offer ') node.textContent = value;
      });
      priceLabels.clear();
    }

    if (active) {
      document.querySelectorAll('[data-kp-pairing]').forEach(pairing => {
        if (!pairings.has(pairing)) pairings.set(pairing, { hidden: pairing.hidden });
        pairing.setAttribute('data-kp-utsav-suppressed-pairing', '');
        pairing.removeAttribute('data-kp-pairing');
        pairing.hidden = true;
        pairing.classList.remove('has-sticky-footer');
        pairing.querySelectorAll('.kp-pairing__checkout').forEach(checkout => checkout.classList.remove('is-sticky'));
      });
      document.body.classList.remove('kp-pairing-sticky-active');
    } else if (pairings.size) {
      const restored = [];
      pairings.forEach((state, pairing) => {
        if (!pairing.isConnected) return;
        pairing.setAttribute('data-kp-pairing', '');
        pairing.removeAttribute('data-kp-utsav-suppressed-pairing');
        pairing.hidden = state.hidden;
        restored.push(pairing);
      });
      pairings.clear();
      restored.forEach(pairing => {
        if (!pairing.dataset.kpInitialized && document.querySelector('[data-kp-pdp][data-kp-initialized]')) {
          pairing.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        }
      });
    }
  };

  document.addEventListener('click', event => {
    const legacy = event.target?.closest?.('[data-kp-add-combo], [data-kp-confirm-combo]');
    const link = event.target?.closest?.('[data-kp-utsav-builder-link]');
    if (!link && (!legacy || !isActive())) return;
    const root = (legacy || link).closest('[data-kp-pdp]') || document.querySelector('[data-kp-pdp]');
    if (!root) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void addSelectionToUtsav(root);
  }, true);

  ['kp:add-current-to-combo', 'kp:product-quantity-changed'].forEach(type => {
    document.addEventListener(type, event => {
      if (isActive()) event.stopImmediatePropagation();
    }, true);
  });
  document.addEventListener('change', event => {
    if (isActive() && event.target?.matches?.('[data-kp-variant-select]')) updateLink(event.target.closest('[data-kp-pdp]'));
  });
  document.addEventListener('kp:variant-changed', refresh);
  document.addEventListener('kp:utsav-journey-change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('pageshow', refresh);
  window.KpUtsavPdpJourney = { refresh, addSelectionToUtsav };
  refresh();
})();
